import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import { Node, Path, Flow, Field, Spark } from "@/components/primitives/Primitives";
import SonareGraphic from "@/components/studio/SonareGraphic";
import MaestroGraphic from "@/components/studio/MaestroGraphic";
import {
    AtriumGraphic,
    ConstellationsGraphic,
    MarginaliaGraphic,
} from "@/components/studio/ExplorationGraphics";

// Sonare's tag/line/body are reused verbatim from the Products page copy
// (frontend/src/lib/i18n.js, body.products.items) — no new claims about
// what it does. Maestro's copy below is user-approved.
const foundry = [
    {
        name: "Sonare",
        slug: "sonare",
        tag: "Voice AI",
        status: "Private preview",
        line: "A voice canvas for enterprise conversations.",
        body: "Multilingual voice interfaces tuned to your customers, your compliance, and your acoustics. Real-time transcription, live translation, and intent that stays legible.",
        primitive: "sonare",
    },
    {
        name: "Maestro",
        slug: "maestro",
        tag: "Music AI",
        status: "In development",
        line: "A creative canvas for musical ideas.",
        body: "Maestro explores how artificial intelligence can support composition, arrangement, and musical experimentation—helping ideas take shape while keeping human creativity at the centre.",
        primitive: "maestro",
    },
];

const experiments = [
    { n: "Constellations", d: "A visual grammar for agent plans.", primitive: "constellations" },
    { n: "Wayfinder", d: "Navigation that listens.", primitive: "path" },
    { n: "Currents", d: "Live translation as ambient sound.", primitive: "flow" },
    { n: "Atrium", d: "A quiet room for retrieval.", primitive: "atrium" },
    { n: "Beacons", d: "Small sparks for large decisions.", primitive: "spark" },
    { n: "Marginalia", d: "Editorial annotations for AI output.", primitive: "marginalia" },
];

const Prim = ({ k }) => {
    if (k === "sonare") return <SonareGraphic className="w-full h-40" />;
    if (k === "maestro") return <MaestroGraphic className="w-full h-40" />;
    if (k === "constellations") return <ConstellationsGraphic className="w-full h-40" />;
    if (k === "atrium") return <AtriumGraphic className="w-full h-40" />;
    if (k === "marginalia") return <MarginaliaGraphic className="w-full h-40" />;
    if (k === "node") return <Node size={140} />;
    if (k === "path") return <Path className="w-full h-24" />;
    if (k === "flow") return <Flow className="w-full h-32" />;
    if (k === "field") return <Field className="w-40 h-40" />;
    return <Spark className="w-32 h-32" />;
};

export default function Studio() {
    return (
        <Layout title="Studio — Ventara" description="Ventara Studio: where Sonare, Maestro, and other prototypes take shape, alongside the research and experiments informing what we build next.">
            <PageHero i18nKey="studio"
                eyebrow="Ventara Studio"
                title={<>Where ideas become<br />intelligent products.</>}
                lead="The Studio is where research, ideas, and early prototypes are given room to become something real — refined patiently, tested quietly, and shaped into products only when they are ready."
            />

            <Section className="pb-24">
                <Reveal>
                    <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">In the Foundry</h2>
                    <p className="prose-body text-lg mt-6 max-w-2xl">Products and prototypes taking shape inside Ventara.</p>
                </Reveal>
                <div className="grid md:grid-cols-2 gap-8 mt-16" data-testid="studio-foundry">
                    {foundry.map((p, i) => (
                        <Reveal key={p.name} delay={i * 0.08}>
                            <article
                                className="rounded-3xl border border-border p-10 sm:p-12 h-full flex flex-col hover:border-foreground/30 transition-colors"
                                data-testid={`studio-foundry-${p.slug}`}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    {p.tag ? <p className="label">{p.tag}</p> : <span />}
                                    <span className="px-3 py-1 rounded-full border border-border text-xs text-muted-foreground shrink-0">
                                        {p.status}
                                    </span>
                                </div>
                                <div className="h-40 flex items-center justify-center text-foreground/70 opacity-80 mt-8">
                                    <Prim k={p.primitive} />
                                </div>
                                <div className="mt-10">
                                    <h3 className="font-serif font-light text-4xl tracking-tight">{p.name}</h3>
                                    {p.line && <p className="font-serif text-xl mt-4 text-muted-foreground italic">{p.line}</p>}
                                    <p className="prose-body mt-4">{p.body}</p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </Section>

            <Section className="pb-32 pt-16 border-t border-border">
                <Reveal>
                    <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">Explorations</h2>
                    <p className="prose-body text-lg mt-6 max-w-2xl">Research, design studies, and technical experiments informing what we build next.</p>
                </Reveal>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
                    {experiments.map((e, i) => (
                        <Reveal key={e.n} delay={(i % 3) * 0.06}>
                            <article className="rounded-3xl border border-border p-10 h-full flex flex-col hover:border-foreground/30 transition-colors" data-testid={`studio-${e.n.toLowerCase()}`}>
                                <div className="h-40 flex items-center justify-center text-foreground/70 opacity-80">
                                    <Prim k={e.primitive} />
                                </div>
                                <div className="mt-10">
                                    <p className="label">Experiment 0{i + 1}</p>
                                    <h3 className="font-serif text-3xl mt-4 tracking-tight">{e.n}</h3>
                                    <p className="prose-body text-sm mt-3">{e.d}</p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </Section>
        </Layout>
    );
}
